/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { flatMapDeep } from 'src/core/utils/array';
import { createConversationFeatureStore } from 'src/luis/core/stores/conversationFeatureStore';
import { Feature } from 'src/luis/modules/features/types/Feature';
import { FeatureSwagger, RegularFeatureSwagger } from 'src/luis/modules/features/types/FeatureSwagger';
import { ModelType } from 'src/luis/types/ModelType';
import { Resource } from 'src/luis/types/Resource';
import { ModelStore } from 'src/luis/types/stores/core/ModelStore';
import { CompositeEntityStore } from 'src/luis/types/stores/entities/CompositeEntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { IntentStore } from 'src/luis/types/stores/intents/IntentStore';

/**
 * Check if the swagger type is a phrase list of model feature
 *
 * @param featureSwagger The swagger object to check
 */
export const isPhraseList = (featureSwagger: FeatureSwagger): featureSwagger is RegularFeatureSwagger =>
    (<RegularFeatureSwagger>featureSwagger).featureName !== undefined;

/**
 * Converts the given feature to a conversation feature store.
 *
 * @param feature The feature to convert.
 */
export const featureSwaggerToConversationFeatureStore = (feature: Feature) =>
    createConversationFeatureStore(feature.id, {
        typeId: feature.typeId === 'feature' ? 'feature' : 'model',
        name: feature.name,
        isRequired: feature.isRequired
    });

/**
 * Gets the models that have the given feature set as a feature
 * to that model.
 *
 * @param feature The feature to check the models for.
 * @param modelsToSearch The models search space (intents, entities).
 */
export const getModelsUsedByFeature = <T extends Resource>(feature: T, modelsToSearch: ModelStore[]): ModelStore[] =>
    modelsToSearch.filter(model => {
        if (model.typeId === ModelType.Intent) {
            return (<IntentStore>model).featureStoreHouse.findById(feature.id) !== undefined;
        }

        if (model.typeId === ModelType.Simple) {
            const entityStores = flatMapDeep([<SimpleEntityStore>model], store => store.children);

            return entityStores.some(entityStore => entityStore.featureStoreHouse.findById(feature.id));
        }

        if (model.typeId === ModelType.Composite) {
            return (<CompositeEntityStore>model).featureStoreHouse.findById(feature.id) !== undefined;
        }

        return false;
    });
